import React from 'react';
import { AiOutlineClose } from "react-icons/ai";
import { RiStackFill, RiStackLine } from "react-icons/ri";
import { BGG_BASE_URL, STATUS } from '../../constants';

/***********************************************************
* CUSTOM RENDERERS FOR BGG LIST
***********************************************************/

export function CheckboxRenderer(props) {
    const isSelected = props.value === STATUS.TRUE;
    const imageUrl = props.data?.item?.image || props.data?.item?.thumbnail || '';
    
    const handleClick = () => {
        // Update the data in the grid
        const newValue = isSelected ? STATUS.FALSE : STATUS.TRUE;
        props.node.setDataValue('selected', newValue);
        
        // Update URL parameters without triggering filter
        if (props.context.updateUrlWithSelectedItems) {
            props.context.updateUrlWithSelectedItems();
        }
    };
    
    return (
        <div onClick={handleClick} className="thumbnail-select-cell">
            {imageUrl ? (
                <img src={imageUrl} alt="" loading="lazy" />
            ) : (
                <span className="thumbnail-placeholder">🎲</span>
            )}
            
            {isSelected && (
                <div className="thumbnail-selected-overlay">
                    <span className="thumbnail-checkmark">✓</span>
                </div>
            )}
        </div>
    );
}

export function GameLinkRenderer(props) {
    // Handle modal opening - props from cellRendererParams are in props.params
    const { openModal, setModalGameData } = props || {};
    const handleClick = () => {
        console.log(openModal, setModalGameData)
        if (openModal && setModalGameData) {
            setModalGameData(props.data);
            openModal();
        }
    };

    const viewClick = () => {
        window.open(BGG_BASE_URL.concat(props.data.href), '_blank');
    };

    return (
        <div className='game-link-container'>
            <span className='game-link' onClick={handleClick}>
                {props.value}
            </span>
            <button className="secondary-button" onClick={viewClick}>
                <i className="fa-solid fa-arrow-up-right-from-square"></i>
            </button>
        </div>
    );
}

export function SellerCellRenderer(props) {
    const filterModel = props.gridRef?.current?.api?.getFilterModel() ?? {};
    return (
        <>
            <button className="link-button" onClick={props.filterToSeller(props.value)}>{props.value}</button>
            { filterModel.username && filterModel.username.filter === props.value && (
                <button className="icon-button" onClick={props.filterToSeller('')}>
                    <AiOutlineClose />
                </button>
            )}
        </>
    );
}

export function ListingInfoRenderer(props) {
    const handleClick = () => {
        props.setModalGameData(props.data);
        props.openModal();
    };

    const viewClick = () => {
        window.open(BGG_BASE_URL.concat(props.data.href), '_blank');
    };

    return (
        <div className="button-container">
            <button className="secondary-button" onClick={handleClick}>
                Summary
            </button>
            <button className="secondary-button" onClick={viewClick}>
                <i className="fa-solid fa-arrow-up-right-from-square"></i>
            </button>
        </div>
    );
}

export function OtherListingsCellRenderer(props) {

    // if the filter is already set to this game, don't render the button
    if (props.value > 0 && (props.api.getFilterModel().itemName === undefined || (props.api.getFilterModel().itemName && props.api.getFilterModel().itemName.filter !== props.data.item.name))) {
        return (
            <button className='icon-button' onClick={() => {        
                props.api.setFilterModel({
                    ...props.api.getFilterModel(),
                    itemName: {
                        filterType: 'text',
                        type: 'equals',
                        filter: props.data.item.name
                    }
                });
            }}>
                {props.value}
            </button>
        );
    } else if (props.api.getFilterModel().itemName && props.api.getFilterModel().itemName.filter === props.data.item.name) {
        // Return a button to clear the filter
        return (
            <button className='icon-button' onClick={() => {
                props.api.setFilterModel({
                    ...props.api.getFilterModel(),
                    itemName: undefined
                });
            }}>
                <AiOutlineClose />
            </button>
        );
    }
}

export function EndDateRenderer(props) {
    if (!props.value) {
        return <span>-</span>;
    }
    
    try {
        // The value should already be a Date object from our improved findAuctionEndDate function
        const dateObj = props.value;
        
        // Format date to MM/DD/YYYY
        const dateString = dateObj.toLocaleDateString('en-US', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
        });
        
        // Calculate days remaining
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day
        
        const endDate = new Date(dateObj);
        endDate.setHours(23, 59, 59, 999); // Set to end of day
        
        const diffTime = endDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        // Color code based on days remaining
        let color = '#000000'; // Default black
        if (diffDays < 0) {
            color = '#888888'; // Gray for expired
        } else if (diffDays === 0) {
            color = '#ff0000'; // Red for ending today
        } else if (diffDays === 1) {
            color = '#ff6600'; // Orange for ending tomorrow
        } else if (diffDays <= 3) {
            color = '#ffcc00'; // Yellow for ending soon
        }
        
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ height: 'auto', width: 'auto', lineHeight: 'normal' }}>{dateString}</span>
                <span style={{ height: 'auto', width: 'auto', lineHeight: 'normal', color, fontSize: '0.85em', fontWeight: diffDays <= 1 ? 'bold' : 'normal' }}>
                    {diffDays < 0 
                        ? 'Ended' 
                        : diffDays === 0 
                            ? 'Today!' 
                            : diffDays === 1 
                                ? 'Tomorrow!' 
                                : `${diffDays} days`}
                </span>
            </div>
        );
    } catch (e) {
        // If any error occurs, just show a dash
        return <span>-</span>;
    }
}

export function DealScoreRenderer(props) {
    if (!props.value || props.value.score === null || props.value.score === "") {
        // return a button that calculates the score for this one row
        return (
            <button className="secondary-button" onClick={() => {
                props.updateDealScore(props.data);
            }}>Lookup</button>
        )
    } else if (props.value.score === "?") {
        return <span>?</span>;
    } else if (props.value.score === "N/A") {
        return <span>N/A</span>;
    } else if (props.value.score === "Loading...") {
        return <span>Loading...</span>;
    } else {
        // Apply color based on percentage
        let color = "#000";
        const percentage = parseFloat(props.value.score);
        if (percentage > 50) {
            color = "#008800"; // Great deal (more than 50% off average) - green
        } else if (percentage > 10) {
            color = "#00AA00"; // Good deal (more than 10% off average) - light green
        } else if (percentage > -10) {
            color = "#ed8d11"; // Fair deal (within 10% of average) - yellow
        } else {
            color = "#880000"; // Poor deal (more than 110% of average) - red
        }

        return (
            <span>
                ${props.value.averagePrice}
                {props.value.includesExtras && 
                    <span style={{ fontSize: '0.75em', color: '#666', marginLeft: '2px' }}>
                        {props.value.allPriced ? <RiStackFill/> : <RiStackLine/>}
                    </span>}
                <span style={{ color: color, marginLeft: '5px' }}>[{percentage.toFixed(0)}%]</span>
            </span>
        );
    }
}

export function LocationRenderer(props) {
    if (!props.data.location || props.data.location.city === "" || props.data.location.state === "") {
        return <span>-</span>;
    } else {
        return <span>{props.data.location.city}, {props.data.location.state}</span>;
    }
}


export function ResearchButtonRenderer(props) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <button
          className="secondary-button"
          onClick={() => props.context.handleGameSelect(props.data)}
        >
          View Price History
        </button>
      </div>
    );
  }